/**
 * §C.2 · Mi cuenta.
 *
 * ## Es de la persona, no del club
 *
 * Y esa distinción decide qué entra. El **horario de silencio no está aquí y no va a
 * estarlo**: es del club, porque es quien responde de no molestar a ciento veinte
 * casas a las once de la noche, y dejar que cada uno se lo suba dejaría al club sin
 * forma de cumplir su propia promesa. Lo que sí está es lo que solo le importa a uno.
 *
 * Por eso la pantalla cuelga de la cabecera —donde está el correo y el botón de
 * salir— y no de ninguna sección del club: quien tiene dos clubes tiene una sola
 * cuenta.
 *
 * ## La contraseña es solo para el personal del club
 *
 * `PUT /me/password` declara `own` y `adm`: **una familia no tiene contraseña**, entra
 * siempre con el enlace del correo. Esta pantalla cuelga de la raíz y no del panel, así
 * que un tutor puede llegar escribiendo la dirección — y la primera versión le enseñaba
 * un formulario que siempre respondía `403`. Un formulario que no puede funcionar es la
 * definición de una aplicación que parece rota, así que el bloque no se pinta.
 *
 * Se vio abriendo la pantalla con la cuenta recién invitada, no leyendo el contrato.
 *
 * ## Y puede no existir todavía
 *
 * El entrenador que siempre entró por enlace mágico no tiene ninguna, y entonces no se
 * le pide la actual: no hay nada que confirmar. Si la hay, hace falta — una sesión
 * robada de un móvil abierto podría cambiarla y dejar fuera al dueño para siempre.
 *
 * El servidor lo sabe; aquí no, así que el campo de «la actual» se ofrece **opcional y
 * explicado**, en vez de adivinarlo y equivocarse en uno de los dos sentidos.
 *
 * ## Cerrar una sesión ajena es la función, no un efecto secundario
 *
 * La lista existe para que alguien reconozca un aparato que no es suyo y lo eche. Por
 * eso se dice de qué aparato es cada una en palabras y no con el `user_agent` crudo,
 * que no le dice nada a nadie.
 *
 * ## Borrar la cuenta no borra al alumno, y se dice
 *
 * Los datos del menor son del club —él es el responsable del tratamiento y nosotros el
 * encargado—, así que quien decide sobre ellos es él desde §P.8. Una pantalla que
 * dijera «borra todos mis datos» sin esa línea prometería algo que no puede cumplir.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { useSesion, useYo } from '@/shared/auth/sesionContexto';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { SubirFichero } from '@/shared/ui/SubirFichero';
import { cuenta as cuantos, fecha, fechaYHora } from '@/shared/ui/formato';
import {
  aparatoDe,
  ESTADOS_DE_PETICION,
  type PaginaDeAvisos,
  type Preferencias,
  type ResguardoDeDatos,
  type SesionAbierta,
} from '@/cuenta/tipos';

const MINIMO = 10;

export function MiCuenta({ alVolver }: { alVolver: () => void }) {
  const yo = useYo();
  const { salir } = useSesion();
  const cache = useQueryClient();
  const [problema, setProblema] = useState<Problema | null>(null);
  const [hecho, setHecho] = useState<string | null>(null);

  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [motivo, setMotivo] = useState('');

  const avisos = useQuery({
    queryKey: ['mis-avisos'],
    queryFn: async () => {
      const { data, error } = await api.v1.me.notifications.get({
        query: { limit: 20 },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeAvisos;
    },
  });

  const preferencias = useQuery({
    queryKey: ['mis-preferencias'],
    queryFn: async () => {
      const { data, error } = await api.v1.me.preferences.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Preferencias;
    },
  });

  const sesiones = useQuery({
    queryKey: ['mis-sesiones'],
    queryFn: async () => {
      const { data, error } = await api.v1.auth.sessions.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SesionAbierta[];
    },
  });

  const decir = (texto: string) => {
    setProblema(null);
    setHecho(texto);
  };

  const fallo = (e: unknown) => {
    setHecho(null);
    setProblema(aProblema(e));
  };

  const cambiarContrasena = useMutation({
    mutationFn: async () => {
      const r = await api.v1.me.password.put({
        ...(actual.trim() === '' ? {} : { current_password: actual }),
        new_password: nueva,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setActual('');
      setNueva('');
      decir('Contraseña guardada.');
    },
    onError: fallo,
  });

  const ponerAvatar = useMutation({
    mutationFn: async (assetId: string) => {
      const r = await api.v1.me.avatar.put({ asset_id: assetId });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => decir('Foto cambiada.'),
    onError: fallo,
  });

  const quitarAvatar = useMutation({
    mutationFn: async () => {
      const r = await api.v1.me.avatar.delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => decir('Foto quitada.'),
    onError: fallo,
  });

  const guardarPreferencias = useMutation({
    mutationFn: async (p: Preferencias) => {
      const r = await api.v1.me.preferences.put({
        channels: p.channels,
        daily_digest_at: p.daily_digest_at,
        class_reminder_minutes: p.class_reminder_minutes,
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      decir('Preferencias guardadas.');
      void cache.invalidateQueries({ queryKey: ['mis-preferencias'] });
    },
    onError: fallo,
  });

  const marcarLeidos = useMutation({
    mutationFn: async () => {
      const r = await api.v1.me.notifications.read.post({ all: true });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['mis-avisos'] });
    },
    onError: fallo,
  });

  const cerrarSesion = useMutation({
    mutationFn: async (id: string) => {
      const r = await api.v1.auth.sessions({ id }).delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      decir('Sesión cerrada.');
      void cache.invalidateQueries({ queryKey: ['mis-sesiones'] });
    },
    onError: fallo,
  });

  const pedirExportacion = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.v1.me.export.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ResguardoDeDatos;
    },
    onSuccess: (r) =>
      /*
       * Se dice el estado y el plazo, no solo «recibida». El plazo es legal —un mes—
       * y es lo que convierte una promesa en algo que se puede reclamar.
       */
      decir(
        `${ESTADOS_DE_PETICION[r.status]}, del ${fecha(r.requested_at)}.` +
          (r.due_at ? ` Te llega por correo antes del ${fecha(r.due_at)}.` : '')
      ),
    onError: fallo,
  });

  const pedirBorrado = useMutation({
    mutationFn: async () => {
      const r = await api.v1.me['deletion-request'].post(
        motivo.trim() === '' ? {} : { reason: motivo.trim() }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ResguardoDeDatos;
    },
    onSuccess: () => {
      setBorrando(false);
      setMotivo('');
      decir(
        'Petición registrada. Tienes 30 días para cambiar de idea: escríbenos y la paramos.'
      );
    },
    onError: fallo,
  });

  const p = preferencias.data;

  /*
   * `can_open` viene resuelto del servidor y aquí solo se lee, igual que en la
   * puerta. Quien no abre el panel ni el back office es una familia: no tiene
   * contraseña y no puede ponérsela.
   */
  const conContrasena =
    yo.can_open.includes('panel') || yo.can_open.includes('backoffice');

  return (
    <main className="lienzo">
      <section className="formulario">
        <p className="volver">
          <button type="button" className="enlace-fila" onClick={alVolver}>
            ← Volver
          </button>
        </p>

        <h1>Tu cuenta</h1>
        <p className="tenue">
          {yo.user.first_name} {yo.user.last_name ?? ''} · {yo.user.email}
        </p>

        {problema && <PanelError problema={problema} />}
        {hecho && (
          <p className="nota nota-bien" role="status">
            {hecho}
          </p>
        )}

        {/* ── La foto ──────────────────────────────────────────────────── */}
        <h2 className="seccion">Tu foto</h2>

        <p className="tenue">
          Sale en el panel y en la app, al lado de lo que escribes. Es la única
          foto de una persona que guardamos.
        </p>

        <SubirFichero
          uso="profile"
          etiqueta={
            yo.user.avatar_asset_id ? 'Cambiar la foto' : 'Subir una foto'
          }
          alSubir={(asset) => ponerAvatar.mutate(asset.id)}
        />

        {yo.user.avatar_asset_id && (
          <div className="acciones">
            <button
              type="button"
              className="btn-plano"
              disabled={quitarAvatar.isPending}
              onClick={() => quitarAvatar.mutate()}
            >
              Quitar la foto
            </button>
          </div>
        )}

        {/* ── La contraseña ────────────────────────────────────────────── */}
        {conContrasena ? (
          <>
            <h2 className="seccion">Tu contraseña</h2>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                cambiarContrasena.mutate();
              }}
            >
              <div className="rejilla">
                <label>
                  La actual
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={actual}
                    onChange={(e) => setActual(e.target.value)}
                  />
                </label>

                <label>
                  La nueva
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={MINIMO}
                    value={nueva}
                    onChange={(e) => setNueva(e.target.value)}
                  />
                </label>
              </div>

              {/*
            Se explica en vez de adivinar: el servidor sabe si había contraseña y
            aquí no. Quien entra siempre por enlace no tiene ninguna que confirmar.
          */}
              <p className="tenue">
                Diez caracteres o más; una frase que recuerdes vale más que ocho
                símbolos. <strong>Si nunca has puesto una</strong> —entras por
                el enlace del correo— deja el primer campo vacío.
              </p>

              <div className="acciones">
                <button
                  type="submit"
                  className="btn-primario"
                  disabled={
                    nueva.length < MINIMO || cambiarContrasena.isPending
                  }
                >
                  {cambiarContrasena.isPending ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h2 className="seccion">Cómo entras</h2>
            <p className="tenue">
              Con el enlace que te mandamos por correo, y no hace falta nada
              más.{' '}
              <strong>Las contraseñas son para el personal del club</strong>:
              una menos que recordar.
            </p>
          </>
        )}

        {/* ── Los avisos ───────────────────────────────────────────────── */}
        <h2 className="seccion">Tus avisos</h2>

        {avisos.isPending ? (
          <Cargando filas={2} etiqueta="Cargando avisos" />
        ) : avisos.isError || !avisos.data ? (
          <PanelError problema={aProblema(avisos.error)} />
        ) : avisos.data.data.length === 0 ? (
          <p className="tenue">No tienes ningún aviso.</p>
        ) : (
          <>
            {avisos.data.unread_count > 0 && (
              <div className="acciones">
                <span className="pastilla pastilla-espera">
                  {cuantos(avisos.data.unread_count, 'sin leer', 'sin leer')}
                </span>
                <button
                  type="button"
                  className="btn-plano"
                  disabled={marcarLeidos.isPending}
                  onClick={() => marcarLeidos.mutate()}
                >
                  Marcar todos como leídos
                </button>
              </div>
            )}

            <ul className="lista-clara">
              {avisos.data.data.map((a) => (
                <li key={a.id}>
                  {a.read_at === null && <strong>· </strong>}
                  {a.title}
                  <span className="tenue">
                    {a.academy_name && ` · ${a.academy_name}`} ·{' '}
                    {fechaYHora(a.created_at)}
                  </span>
                </li>
              ))}
            </ul>

            {avisos.data.has_more && (
              <p className="tenue">
                Hay más. Los veinte últimos son los que importan aquí; el resto
                vive en la app.
              </p>
            )}
          </>
        )}

        {/* ── Cuándo avisarte ──────────────────────────────────────────── */}
        <h2 className="seccion">Cuándo avisarte</h2>

        {preferencias.isPending || !p ? (
          <Cargando filas={2} etiqueta="Cargando preferencias" />
        ) : (
          <>
            <div className="rejilla">
              <label className="opcion">
                <input
                  type="checkbox"
                  checked={p.channels.push}
                  onChange={(e) =>
                    guardarPreferencias.mutate({
                      ...p,
                      channels: { ...p.channels, push: e.target.checked },
                    })
                  }
                />
                Avisos en el móvil
              </label>

              <label className="opcion">
                <input
                  type="checkbox"
                  checked={p.channels.email}
                  onChange={(e) =>
                    guardarPreferencias.mutate({
                      ...p,
                      channels: { ...p.channels, email: e.target.checked },
                    })
                  }
                />
                Avisos por correo
              </label>
            </div>

            <div className="rejilla">
              <label>
                Resumen diario a las
                <input
                  type="time"
                  value={p.daily_digest_at ?? ''}
                  onChange={(e) =>
                    guardarPreferencias.mutate({
                      ...p,
                      daily_digest_at:
                        e.target.value === '' ? null : e.target.value,
                    })
                  }
                />
              </label>

              <label>
                Recordar la clase (minutos antes)
                <input
                  type="number"
                  min={5}
                  max={1440}
                  value={p.class_reminder_minutes ?? ''}
                  onChange={(e) =>
                    guardarPreferencias.mutate({
                      ...p,
                      class_reminder_minutes:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
              </label>
            </div>

            <p className="tenue">
              Vacío es «no me lo mandes».{' '}
              <strong>El horario de silencio no está aquí</strong>: lo pone cada
              club, porque es quien responde de no molestar a las familias a las
              once de la noche.
            </p>
          </>
        )}

        {/* ── Dónde tienes la sesión abierta ───────────────────────────── */}
        <h2 className="seccion">Dónde has entrado</h2>

        {sesiones.isPending ? (
          <Cargando filas={2} etiqueta="Cargando sesiones" />
        ) : sesiones.isError || !sesiones.data ? (
          <PanelError problema={aProblema(sesiones.error)} />
        ) : (
          <>
            <p className="tenue">
              Si ves un aparato que no reconoces, ciérralo y cambia la
              contraseña.
            </p>

            <ul className="papeles">
              {sesiones.data.map((s) => (
                <li key={s.id}>
                  <div className="aviso-cabeza">
                    <strong>{aparatoDe(s.user_agent)}</strong>
                    {s.ip && <span className="tenue">{s.ip}</span>}
                  </div>
                  <p className="tenue">
                    Desde el {fechaYHora(s.created_at)} · caduca el{' '}
                    {fecha(s.expires_at)}
                  </p>
                  <div className="acciones">
                    <button
                      type="button"
                      className="btn-plano"
                      disabled={cerrarSesion.isPending}
                      onClick={() => cerrarSesion.mutate(s.id)}
                    >
                      Cerrarla
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            {/*
              No se marca «esta es la tuya»: el servidor no dice cuál es la de
              quien pregunta, y adivinarlo por el `user_agent` fallaría justo en
              el caso que importa —dos pestañas del mismo navegador—. Cerrar la
              propia por error se arregla entrando otra vez.
            */}
            <p className="tenue">
              Cerrar la sesión desde la que estás mirando te saca de aquí: se
              arregla entrando otra vez.
            </p>
          </>
        )}

        {/* ── Tus datos ────────────────────────────────────────────────── */}
        <h2 className="seccion">Tus datos</h2>

        <div className="acciones">
          <button
            type="button"
            className="btn-plano"
            disabled={pedirExportacion.isPending}
            onClick={() => pedirExportacion.mutate()}
          >
            Pedir una copia de mis datos
          </button>
        </div>

        <p className="tenue">
          Se prepara aparte y te llega por correo cuando está.{' '}
          <strong>Pedirla dos veces devuelve la misma</strong>: pedirla tres no
          la acelera.
        </p>

        {borrando ? (
          <div className="hueco hueco-confirmar">
            <h3>¿Pedir que borremos tu cuenta?</h3>
            <ul className="lista-clara">
              <li>
                <strong>No se borra nada ahora.</strong> Hay 30 días de plazo, y
                eso no es una demora administrativa: es lo que hace que un
                borrado pedido en caliente sea reversible.
              </li>
              <li>
                <strong>No borra a tus alumnos.</strong> Los datos de un menor
                son del club, que es el responsable de tratarlos; quien decide
                sobre ellos es él, desde su pantalla de cumplimiento.
              </li>
            </ul>

            <label>
              Por qué, si quieres decirlo
              <input
                maxLength={1000}
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </label>

            <div className="hueco-acciones">
              <button
                type="button"
                className="btn-peligro"
                disabled={pedirBorrado.isPending}
                onClick={() => pedirBorrado.mutate()}
              >
                {pedirBorrado.isPending ? 'Enviando…' : 'Sí, pedirlo'}
              </button>
              <button
                type="button"
                className="btn-plano"
                onClick={() => setBorrando(false)}
              >
                Dejarlo
              </button>
            </div>
          </div>
        ) : (
          <div className="acciones">
            <button
              type="button"
              className="btn-plano"
              onClick={() => setBorrando(true)}
            >
              Pedir que borremos mi cuenta
            </button>
          </div>
        )}

        <h2 className="seccion">Salir</h2>
        <div className="acciones">
          <button
            type="button"
            className="btn-plano"
            onClick={() => void salir()}
          >
            Cerrar esta sesión
          </button>
        </div>
      </section>
    </main>
  );
}
